/**
 * Editable Employee Master grid — spreadsheet-style, every field editable,
 * salary columns calculated live with the exact payroll formulas, individual +
 * bulk editing, and Save & Lock (whole table lock + per-row padlock).
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Save, Lock, Unlock, Loader2, ArrowLeft, Search, RotateCcw, ArrowDownToLine,
  Wand2, AlertTriangle, Table2,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API_URL } from '../../lib/apiBase';
import { safeJson } from '../../lib/safeJson';
import { computeMaster } from '../../lib/salaryCalc';
import { fullName } from '../../lib/fullName';
import { GROUPS, fieldsForGroup, EDITABLE_FIELDS, COMBO_FIELDS, mergeOptions } from './masterFields';

const headers = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

// field.w ('w-24') → a real min-width, because table auto-layout ignores width utilities
const W_REM = { 'w-16': 4, 'w-20': 5, 'w-24': 6, 'w-28': 7, 'w-32': 8, 'w-36': 9, 'w-40': 10,
  'w-44': 11, 'w-48': 12, 'w-52': 13, 'w-56': 14 };
const minW = (f) => `${W_REM[f.w] || 6}rem`;

const SELECT_CLS = 'h-7 rounded border border-slate-200 bg-white px-1 text-[11px] focus:border-[#DC2626] focus:outline-none disabled:bg-slate-100';
const INPUT_CLS = 'h-7 w-full rounded border px-1.5 text-[11px] focus:outline-none focus:border-[#DC2626] disabled:bg-slate-100 '
  + '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

function GridCell({ field, value, dirty, disabled, options, onChange, testid }) {
  const common = {
    disabled,
    'data-testid': testid,
    className: `${INPUT_CLS} ${dirty ? 'border-amber-500 bg-amber-50 font-semibold' : 'border-slate-200'}`,
  };
  if (field.type === 'bool') {
    return (
      <input type="checkbox" checked={value === true} disabled={disabled}
        data-testid={testid}
        onChange={(e) => onChange(e.target.checked)}
        className={`w-4 h-4 rounded accent-[#DC2626] ${dirty ? 'ring-2 ring-amber-500' : ''}`} />
    );
  }
  if (field.type === 'select') {
    const opts = field.options || options[field.optionsFrom] || [];
    const list = value && !opts.includes(value) ? [value, ...opts] : opts;
    return (
      <select value={value ?? ''} disabled={disabled} data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className={`${SELECT_CLS} w-full ${dirty ? 'border-amber-500 bg-amber-50 font-semibold' : ''}`}>
        <option value="">—</option>
        {list.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === 'combo') {
    // pick a known master value OR type a brand-new one
    return (
      <input list={`dl-${field.key}`} value={value ?? ''}
        onChange={(e) => onChange(e.target.value)} {...common} />
    );
  }
  if (field.type === 'number') {
    return (
      <input type="number" step={field.step || 1} value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        {...common} />
    );
  }
  return (
    <input type={field.type === 'date' ? 'date' : field.type === 'time' ? 'time' : 'text'}
      value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...common} />
  );
}

export function EmployeeMasterGrid({ entityId, onBack }) {
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState({});
  const [lock, setLock] = useState({ locked: false });
  const [perms, setPerms] = useState({ can_edit: false, can_edit_salary: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [group, setGroup] = useState('salary');
  const [search, setSearch] = useState('');
  const [dept, setDept] = useState('all');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [bulkField, setBulkField] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [claCfg, setClaCfg] = useState(null);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/employees/master-grid?entity_id=${entityId}&include_inactive=${includeInactive}`,
        { headers: headers(), credentials: 'include' });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Could not load the master data');
      setRows(d.rows || []);
      setOptions(mergeOptions({
        ...(d.options || {}),
        reports_to: [...new Set((d.rows || []).map((r) => fullName(r)).filter(Boolean))].sort(),
        specializations: [...new Set((d.rows || []).map((r) => r.specialization).filter(Boolean))].sort(),
      }));
      setLock(d.lock || { locked: false });
      setPerms({ can_edit: d.can_edit, can_edit_salary: d.can_edit_salary });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [entityId, includeInactive]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!entityId) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/payroll/cla-settings`, { headers: headers(), credentials: 'include' });
        if (!res.ok) return;
        const d = await safeJson(res);
        const row = (Array.isArray(d) ? d : []).find((r) => r.entity_id === entityId);
        setClaCfg(row ? { enabled: row.enabled, teaching: row.teaching, non_teaching: row.non_teaching } : null);
      } catch { /* preview falls back to default 800/400 */ }
    })();
  }, [entityId]);

  const fields = useMemo(() => fieldsForGroup(group), [group]);
  const dirtyCount = useMemo(
    () => Object.values(edits).reduce((n, f) => n + Object.keys(f).length, 0), [edits]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (dept !== 'all' && (r.department || '') !== dept) return false;
      if (!q) return true;
      return [r.first_name, r.middle_name, r.last_name, r.email, r.employee_id, r.department,
        r.designation].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [rows, search, dept, group]);

  const valueOf = (row, key) => {
    const e = edits[row.employee_id];
    return e && key in e ? e[key] : row[key];
  };
  const isDirty = (row, key) => !!(edits[row.employee_id] && key in edits[row.employee_id]);

  const setCell = (row, key, value) => {
    setEdits((prev) => {
      const forRow = { ...(prev[row.employee_id] || {}) };
      if (row[key] === value || (row[key] == null && value === '')) delete forRow[key];
      else forRow[key] = value;
      const next = { ...prev };
      if (Object.keys(forRow).length) next[row.employee_id] = forRow;
      else delete next[row.employee_id];
      return next;
    });
  };

  const rowLocked = (row) => lock.locked || row.master_locked === true || !perms.can_edit;

  const toggleRow = (id) => setSelected((p) => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const applyBulk = () => {
    if (!bulkField) { toast.error('Pick the field to fill'); return; }
    const targets = filtered.filter((r) => selected.has(r.employee_id) && !rowLocked(r));
    if (!targets.length) { toast.error('Tick the rows to apply to'); return; }
    const def = EDITABLE_FIELDS.find((f) => f.key === bulkField);
    let v = bulkValue;
    if (def.type === 'number') v = bulkValue === '' ? 0 : Number(bulkValue);
    if (def.type === 'bool') v = bulkValue === 'true';
    targets.forEach((r) => setCell(r, bulkField, v));
    toast.success(`${def.label} set on ${targets.length} employee(s) — press Save to write it`);
  };

  const copyDown = () => {
    const targets = filtered.filter((r) => selected.has(r.employee_id));
    if (targets.length < 2) { toast.error('Tick the source row plus at least one more'); return; }
    const [src, ...rest] = targets;
    const keys = bulkField
      ? [bulkField]
      : fields.filter((f) => !f.calc && f.key !== 'email').map((f) => f.key);
    let n = 0;
    rest.filter((r) => !rowLocked(r)).forEach((r) => {
      keys.forEach((k) => { setCell(r, k, valueOf(src, k)); n++; });
    });
    toast.success(`Copied ${keys.length} field(s) from ${fullName(src)} to ${rest.length} row(s) (${n} cells)`);
  };

  const save = async (lockAfter) => {
    const changes = Object.entries(edits).map(([employee_id, f]) => ({ employee_id, fields: f }));
    if (!changes.length) { toast.error('Nothing has been edited yet'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/employees/master-grid/save`, {
        method: 'POST', headers: headers(), credentials: 'include',
        body: JSON.stringify({ changes, lock_after: !!lockAfter }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Save failed');
      toast.success(d.message);
      (d.failed || []).forEach((f) => toast.error(`${f.name || f.employee_id}: ${f.reason}`));
      if (!d.failed?.length) setEdits({});
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleTableLock = async () => {
    try {
      const res = await fetch(`${API_URL}/api/employees/master-lock`, {
        method: 'POST', headers: headers(), credentials: 'include',
        body: JSON.stringify({ entity_id: entityId, action: lock.locked ? 'unlock' : 'lock' }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const toggleRowLock = async (row) => {
    try {
      const res = await fetch(`${API_URL}/api/employees/${row.employee_id}/row-lock`, {
        method: 'POST', headers: headers(), credentials: 'include',
        body: JSON.stringify({ locked: !row.master_locked }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const bulkDef = EDITABLE_FIELDS.find((f) => f.key === bulkField);

  return (
    <div className="space-y-4" data-testid="employee-master-grid">
      {COMBO_FIELDS.map((f) => (
        <datalist key={f.key} id={`dl-${f.key}`}>
          {(options[f.optionsFrom] || []).map((o) => <option key={o} value={o} />)}
        </datalist>
      ))}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Table2 className="w-6 h-6 text-[#DC2626]" />
            Employee Master Data
          </h1>
          <p className="text-sm text-slate-500">
            Edit any field, the salary columns calculate themselves, then Save &amp; Lock.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="master-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Staff Directory
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setEdits({}); toast.success('Edits discarded'); }}
            disabled={!dirtyCount} data-testid="master-discard-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Discard
          </Button>
          <Button variant="outline" size="sm" onClick={toggleTableLock} data-testid="master-lock-btn">
            {lock.locked ? <Unlock className="w-4 h-4 mr-1" /> : <Lock className="w-4 h-4 mr-1" />}
            {lock.locked ? 'Unlock table' : 'Lock table'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => save(false)} disabled={saving || !dirtyCount}
            data-testid="master-save-btn" className="border-[#DC2626] text-[#DC2626] hover:bg-red-50">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save {dirtyCount ? `(${dirtyCount})` : ''}
          </Button>
          <Button size="sm" onClick={() => save(true)} disabled={saving || !dirtyCount}
            data-testid="master-save-lock-btn" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white">
            <Lock className="w-4 h-4 mr-1" /> Save &amp; Lock
          </Button>
        </div>
      </div>

      {lock.locked && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 flex items-center gap-2"
          data-testid="master-locked-banner">
          <Lock className="w-4 h-4" />
          Master data is LOCKED{lock.locked_by_name ? ` by ${lock.locked_by_name}` : ''} — unlock the table to edit anything.
        </div>
      )}
      {!perms.can_edit_salary && (
        <div className="rounded-md border border-slate-300 bg-slate-50 p-2 text-xs text-slate-700 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> You can edit personal fields only — salary fields need the
          “edit salary” permission.
        </div>
      )}

      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {GROUPS.map((g) => (
              <button key={g.key} type="button" onClick={() => setGroup(g.key)}
                data-testid={`master-group-${g.key}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  group === g.key
                    ? 'bg-[#DC2626] border-[#DC2626] text-white'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-[#DC2626]'
                }`}>
                {g.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, ID, department…" className="h-8 pl-8 text-xs"
                data-testid="master-search" />
            </div>
            <select value={dept} onChange={(e) => setDept(e.target.value)}
              className={`${SELECT_CLS} h-8 w-44`} data-testid="master-dept-filter">
              <option value="all">All departments</option>
              {(options.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-slate-600">
              <input type="checkbox" checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="w-3.5 h-3.5 accent-[#DC2626]" data-testid="master-include-inactive" />
              Show inactive
            </label>
            <Badge variant="outline" className="text-[10px]" data-testid="master-row-count">
              {filtered.length} of {rows.length} staff
            </Badge>
            {dirtyCount > 0 && (
              <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid="master-dirty-count">
                {dirtyCount} unsaved change(s)
              </Badge>
            )}
          </div>

          {/* ---- bulk bar ---- */}
          <div className="rounded-md border border-slate-300 bg-slate-50 p-2 flex flex-wrap items-center gap-2"
            data-testid="master-bulk-bar">
            <span className="text-[11px] font-semibold text-slate-700 flex items-center gap-1.5">
              <Wand2 className="w-3.5 h-3.5 text-[#DC2626]" /> Apply to {selected.size} selected
            </span>
            <select value={bulkField} onChange={(e) => { setBulkField(e.target.value); setBulkValue(''); }}
              className={`${SELECT_CLS} h-8 w-48`} data-testid="master-bulk-field">
              <option value="">Choose a field…</option>
              {EDITABLE_FIELDS.map((f) => (
                <option key={f.key} value={f.key}>{GROUPS.find(g => g.key === f.group)?.label} › {f.label}</option>
              ))}
            </select>
            {bulkDef?.type === 'bool' ? (
              <select value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}
                className={`${SELECT_CLS} h-8 w-28`} data-testid="master-bulk-value">
                <option value="">—</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            ) : bulkDef?.type === 'select' ? (
              <select value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}
                className={`${SELECT_CLS} h-8 w-44`} data-testid="master-bulk-value">
                <option value="">—</option>
                {(bulkDef.options || options[bulkDef.optionsFrom] || []).map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            ) : bulkDef?.type === 'combo' ? (
              <Input value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}
                list={`dl-${bulkDef.key}`} placeholder="Pick or type a new value"
                className="h-8 w-48 text-xs" data-testid="master-bulk-value" />
            ) : (
              <Input value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}
                type={bulkDef?.type === 'number' ? 'number' : bulkDef?.type === 'date' ? 'date' : 'text'}
                placeholder="Value" className="h-8 w-36 text-xs" disabled={!bulkField}
                data-testid="master-bulk-value" />
            )}
            <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={applyBulk}
              disabled={lock.locked || !perms.can_edit} data-testid="master-bulk-apply">
              Fill selected rows
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={copyDown}
              disabled={lock.locked || !perms.can_edit} data-testid="master-bulk-copydown">
              <ArrowDownToLine className="w-3.5 h-3.5 mr-1" /> Copy down from first row
            </Button>
            <span className="text-[10px] text-slate-500">
              Nothing is written until you press Save. Without a chosen field, Copy down copies every
              field of this tab.
            </span>
          </div>

          {loading ? (
            <div className="space-y-2">
              {[...Array(6)].map((_, i) => <div key={i} className="h-8 bg-slate-100 rounded animate-pulse" />)}
            </div>
          ) : (
            <div className="overflow-auto max-h-[70vh] border border-slate-200 rounded"
              data-testid="master-grid-scroll">
              <table className="text-[11px] w-max">
                <TableHeader className="sticky top-0 z-20 bg-slate-100">
                  <TableRow>
                    <TableHead className="w-8 sticky left-0 bg-slate-100 z-30">
                      <input type="checkbox" className="w-3.5 h-3.5 accent-[#DC2626]"
                        data-testid="master-select-all"
                        checked={filtered.length > 0 && filtered.every((r) => selected.has(r.employee_id))}
                        onChange={(e) => setSelected(e.target.checked
                          ? new Set(filtered.map((r) => r.employee_id)) : new Set())} />
                    </TableHead>
                    <TableHead className="sticky left-8 bg-slate-100 z-30 min-w-[190px]">Employee</TableHead>
                    {fields.map((f) => (
                      <TableHead key={f.key} style={{ minWidth: minW(f) }}
                        className={`whitespace-nowrap text-[10px] uppercase tracking-wide ${f.calc ? 'bg-indigo-50 text-indigo-700' : ''} ${f.strong ? 'font-bold' : ''}`}>
                        {f.label}{f.calc ? ' ƒ' : ''}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((row) => {
                    const merged = { ...row, ...(edits[row.employee_id] || {}) };
                    const calc = computeMaster(merged, null, claCfg);
                    const disabled = rowLocked(row);
                    return (
                      <TableRow key={row.employee_id}
                        className={edits[row.employee_id] ? 'bg-amber-50/40' : ''}
                        data-testid={`master-row-${row.employee_id}`}>
                        <TableCell className="sticky left-0 bg-white z-10">
                          <input type="checkbox" className="w-3.5 h-3.5 accent-[#DC2626]"
                            checked={selected.has(row.employee_id)}
                            onChange={() => toggleRow(row.employee_id)}
                            data-testid={`master-select-${row.employee_id}`} />
                        </TableCell>
                        <TableCell className="sticky left-8 bg-white z-10 min-w-[190px]">
                          <div className="flex items-center gap-1.5">
                            <button type="button" onClick={() => toggleRowLock(row)}
                              title={row.master_locked ? 'Row locked — click to unlock' : 'Lock this row'}
                              data-testid={`master-rowlock-${row.employee_id}`}
                              className={row.master_locked ? 'text-[#DC2626]' : 'text-slate-300 hover:text-slate-600'}>
                              {row.master_locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                            </button>
                            <div className="leading-tight">
                              <p className="font-medium text-slate-800">{fullName(merged)}</p>
                              <p className="text-[9px] text-slate-400">{row.employee_id} · {row.department || '—'}</p>
                            </div>
                          </div>
                        </TableCell>
                        {fields.map((f) => (
                          <TableCell key={f.key} style={{ minWidth: minW(f) }}
                            className={`px-1 py-0.5 ${f.calc ? 'bg-indigo-50/60' : ''}`}>
                            {f.calc ? (
                              <span className={`font-mono ${f.strong ? 'font-bold text-indigo-900' : 'text-indigo-800'}`}
                                data-testid={`master-calc-${f.key}-${row.employee_id}`}>
                                {calc[f.key]?.toLocaleString('en-IN')}
                              </span>
                            ) : (
                              <GridCell field={f} options={options}
                                value={valueOf(row, f.key)}
                                dirty={isDirty(row, f.key)}
                                disabled={disabled || (f.salary && !perms.can_edit_salary)}
                                onChange={(v) => setCell(row, f.key, v)}
                                testid={`master-cell-${f.key}-${row.employee_id}`} />
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </table>
            </div>
          )}
          <p className="text-[10px] text-slate-500">
            ƒ columns are calculated with the same formulas payroll uses: Gross Basic = Basic + AGP ·
            DA = Gross Basic × DA% · HRA = 10% of Gross Basic · CLA = ₹800 Teaching / ₹400 Non-Teaching ·
            PF = 12% of (Basic + DA) capped at ₹1,800 · Prof. Tax by gender slab (₹300 in February) ·
            ESIC = 0.75% of gross when gross ≤ ₹21,000.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default EmployeeMasterGrid;
